// The browser-tab icon follows the section (public/icons/<set>/). The server
// decides which set (Web::BaseController#favicon_set): the layout renders it on
// a full page load, and every Inertia response carries it as the shared prop
// `favicon`. Inertia never re-renders the layout on in-app navigation, so this
// swaps the href of the two <link data-favicon> tags instead.
//
// A page can also put another set on the tab for a while (setFaviconOverride):
// /pomodoro shows the timer's mode. The two are kept apart and the tab shows
// `override ?? section`, so leaving such a page ends up right whichever comes
// first, the page's unmount clearing its override or the navigate event
// bringing the next section.

// Every set a page may override with. test/site_icon_files_test.rb reads this
// list to check that each one has its files under public/icons/.
export const FAVICON_OVERRIDES = ['pomodoro-work', 'pomodoro-break'] as const
export type FaviconOverride = (typeof FAVICON_OVERRIDES)[number]

let section: string | null = null
let override: FaviconOverride | null = null

export function applyFavicon(set: unknown): void {
  // A partial reload with `only` filters out shared props too. Without a value,
  // keep whatever is showing rather than guess.
  if (typeof set !== 'string') return

  section = set
  render()
}

export function setFaviconOverride(set: FaviconOverride | null): void {
  // Before the first Inertia event the section is only known from what the
  // server rendered; read it now, before an override replaces it on the tab.
  section ??= renderedSet()
  override = set
  render()
}

function render(): void {
  const set = override ?? section
  if (!set) return

  setHref('ico', `/icons/${set}/favicon.ico`)
  setHref('svg', `/icons/${set}/icon.svg`)
}

function renderedSet(): string | null {
  const href = document.querySelector('link[data-favicon="svg"]')?.getAttribute('href')
  return href?.match(/^\/icons\/([^/]+)\//)?.[1] ?? null
}

function setHref(kind: 'ico' | 'svg', href: string): void {
  const link = document.querySelector<HTMLLinkElement>(`link[data-favicon="${kind}"]`)
  // Compare the attribute, not link.href: the property is the resolved absolute
  // URL, never equal to the relative path, so every call would rewrite it and
  // make the browser refetch the icon.
  if (link && link.getAttribute('href') !== href) link.setAttribute('href', href)
}
